import { Banner, BlockStack, Box } from '@shopify/polaris';
import { LibraryNotification, useLibraryNotificationsStore } from './stores/libraryNotificationsStore';
import { memo, useMemo, useState } from 'react';
import NotificationsModal from './NotificationsModal';
import { useShallow } from 'zustand/react/shallow';
import { AssetCreationToolName, MediaAssetStatus, StandaloneMediaAsset } from '@/types/media';
import { useAllAiCreations } from '../my-creations-library/hooks/useAllAiCreations';
import { AICreationType } from '@/api/list-ai-creations';
import { differenceInMinutes } from 'date-fns';
import { useCurrentApp } from '@/stores/userDataStore';
import DismissibleBanner from '@/components/DismissibleBanner';
import { SessionBanner } from '@/types/banners';

const sourceDisplayNames = {
  studio_video: 'Studio Video',
  media_asset: 'Media Asset',
};

export const LibraryNotificationsContainer = memo(() => {
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { appKey } = useCurrentApp();

  const { notifications, dismissedNotifications, dismissNotification } = useLibraryNotificationsStore(
    useShallow((state) => state)
  );

  const { aiCreations } = useAllAiCreations({ appKey });

  const assetsWithSlowGenerations = useMemo(() => {
    return aiCreations.reduce<StandaloneMediaAsset[]>((acc, { item, type }) => {
      const isStudioVideo = type === AICreationType.studioVideo;

      if (
        (isStudioVideo || item.createdWith.toolName === AssetCreationToolName.animateImage) &&
        item.status === MediaAssetStatus.PROCESSING &&
        differenceInMinutes(new Date(), new Date(item.updatedAt)) < 60
      ) {
        acc.push(item);
      }
      return acc;
    }, []);
  }, [aiCreations]);

  const hasSlowGenerationsProcessing = useMemo(() => {
    return assetsWithSlowGenerations.length > 0;
  }, [assetsWithSlowGenerations]);

  const liveNotifications = useMemo(() => {
    const liveNotificationIds = new Set(dismissedNotifications || []);
    return notifications.filter((notification) => !liveNotificationIds.has(notification.id));
  }, [notifications, dismissedNotifications]);

  const groupedNotifications = useMemo(() => {
    const groups = liveNotifications.reduce(
      (acc, notification) => {
        if (!acc[notification.source]) {
          acc[notification.source] = [];
        }
        acc[notification.source].push(notification);
        return acc;
      },
      {} as Record<string, LibraryNotification[]>
    );

    return Object.entries(groups).map(([source, notifications]) => ({
      source,
      tone: notifications[0].tone,
      title: notifications[0].title,
      notifications,
      count: notifications.length,
    }));
  }, [liveNotifications]);

  const handleDismissGroup = (notifications: LibraryNotification[]) => {
    notifications.forEach((notification) => dismissNotification(notification.id));
  };

  const handleShowDetails = (source: string) => {
    setSelectedSource(source);
    setIsModalOpen(true);
  };

  return (
    <Box paddingBlockEnd={hasSlowGenerationsProcessing || groupedNotifications.length > 0 ? '400' : undefined}>
      <BlockStack>
        {groupedNotifications.map(({ source, title, tone, notifications, count }) => (
          <Banner
            tone={tone}
            key={source}
            title={title}
            action={{
              content: 'View details',
              onAction: () => handleShowDetails(source),
            }}
            onDismiss={() => handleDismissGroup(notifications)}
          >
            {count === 1 ? '1 notification' : `${count} notifications`}
          </Banner>
        ))}

        {selectedSource && (
          <NotificationsModal
            isOpen={isModalOpen}
            setIsOpen={setIsModalOpen}
            notifications={liveNotifications.filter((n) => n.source === selectedSource)}
            source={sourceDisplayNames[selectedSource as keyof typeof sourceDisplayNames] || selectedSource}
          />
        )}

        <DismissibleBanner
          tone="warning"
          title="Due to increased demand, generations may take a little longer."
          showBanner={hasSlowGenerationsProcessing}
          sessionBanner={SessionBanner.MediaAssetProcessing}
        ></DismissibleBanner>
      </BlockStack>
    </Box>
  );
});

LibraryNotificationsContainer.displayName = 'LibraryNotificationsContainer';
